import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
    Component,
    computed,
    ContentChildren,
    DestroyRef,
    effect,
    inject,
    Input,
    input,
    OnInit,
    output,
    PLATFORM_ID,
    QueryList,
    signal,
    TemplateRef,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Skeleton, Table, TableColumn } from '@ntv360/component-pantry';
import { NtvTableCellDirective } from '@shared/components/table/directives/ntv-cell.directive';
import { SVG_ICONS } from '@shared/constants';
import { CopyDirective } from '@shared/directives';
import { TableActionsComponent } from './components/table-actions/table-actions.component';
import { TableActionService } from './components/table-actions/table-actions.service';

@Component({
    selector: 'app-table',
    standalone: true,
    imports: [Table, TableActionsComponent, CommonModule, CopyDirective, Skeleton],
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss',
})
export class TableComponent implements OnInit {
    /**
     * Minimum skeleton row count rendered during loading states.
     */
    private readonly MIN_SKELETON_ROWS = 12;

    /**
     * Flag indicating if the component is running in a browser environment.
     */
    public isBrowser = signal(isPlatformBrowser(inject(PLATFORM_ID)));

    /**
     * Table actions service responsible for the emitted clicks (if there are)
     */
    private _tableActionsService = inject(TableActionService);

    /**
     * Reference to the underlying pantry table. Used to trigger its own
     * `showMore()`/`resetPagination()` so its internal page counter and
     * `tableStateChange` emissions stay correct — some consumers (e.g.
     * Playlists) track their own pagination purely off that event.
     */
    protected ntvTable = viewChild<Table>('ntvTable');

    /**
     * Inject destroy ref to destroy subscriptions
     */
    private destroyRef = inject(DestroyRef);

    /**
     * List of templates passed from parent to customize specific columns.
     */
    @ContentChildren(NtvTableCellDirective)
    private _cellTemplates!: QueryList<NtvTableCellDirective>;

    /**
     * Title of the table, displayed in the table header.
     */
    public tableTitle = input<string>('Data Table');

    /**
     * Flag indicating if the table should have checkboxes.
     */
    @Input() hasCheckBox: boolean = false;

    /**
     * Reference to the collection of SVG icon names used in the table.
     */
    public icons: Record<string, string> = SVG_ICONS;

    /**
     * Path to the SVG sprite file containing icons used in the table.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Height of the table container.
     */
    public tableHeight = input<string>('700px');

    /**
     * Vertical padding for table body cells.
     */
    public paddingY = input<string>('14px');

    /**
     * Horizontal padding for table body cells.
     */
    public paddingX = input<string>('15px');

    /**
     * Column definitions for the table.
     */
    public columns = input<TableColumn[]>([]);

    /**
     * Whether to show the table column settings controller. Defaults to `false`
     */
    public showColumnSettings = input<boolean>(false);

    /**
     * Whether to show the filter icons in the table header. Defaults to `false`
     */
    public filterEnabled = input<boolean>(false);

    /**
     * Data to be displayed in the table.
     */
    public data = input<any[]>([]);

    /**
     * Total data length for the pagination
     */
    public totalDataLength = input<number>(0);

    /**
     * Whether the table is in a loading state.
     */
    public loading = input<boolean>(false);

    /**
     * Whether the column filtering is triggered. We don't re-render the table entirely, only its body so the filtering states would retain
     */
    public isColumnFiltering = input<boolean>(false);

    /**
     * Shows an animated overlay on top of the existing table (used for show-more and filter loads).
     */
    public loadingOverlay = input<boolean>(false);

    /**
     * Internal loading state for show-more interactions.
     */
    private _showMoreLoading = signal<boolean>(false);

    /**
     * Tracks whether the parent loading signal flipped true during a show-more cycle.
     * Prevents the reset effect from firing on the same tick the show-more started,
     * before the parent has set its loading flag.
     */
    private _showMoreSawLoading = false;

    /**
     * Final overlay visibility combining parent input and internal show-more loading.
     */
    public resolvedLoadingOverlay = computed(
        () => this.loadingOverlay() || this._showMoreLoading(),
    );

    /**
     * Whether to show the item count in the table.
     */
    public showItemCount = input<boolean>(true);

    /**
     * The number of rows a single page/fetch holds. Used only to decide whether
     * "Show Less" should render — visible once more than one page's worth of rows
     * has been accumulated via "Show More".
     */
    public pageSize = input<number>(10);

    /**
     * Whether more than one page's worth of rows is currently loaded, i.e.
     * whether "Show Less" has anything to collapse back from.
     */
    public canShowLess = computed(() => this.data().length > this.pageSize());

    /**
     * Whether there are more rows to load than currently shown. Drives which
     * single toggle button ("Show More" vs "Show Less") the footer renders —
     * only one is ever visible at a time.
     */
    public hasMoreItems = computed(() => this.data().length < this.totalDataLength());

    /**
     * Emits when "Show Less" is clicked, requesting the parent collapse the
     * accumulated rows back to the first page.
     */
    public showLessRequested = output<void>();

    /**
     * Emits when an action button is clicked.
     * Contains the action name and the row data.
     */
    public actionClick = output<{ action: string; row: any }>();

    /**
     * Emits when a clickable cell (hasRedirect: true) is clicked.
     * Contains the field name, cell value, and row data.
     */
    public cellClick = output<{ field: string; value: any; row: any }>();

    /**
     * Emits when row selection changes.
     * Contains an array of selected row data.
     */
    public selectionChange = output<any[]>();

    /**
     * Emits when the show more button is clicked.
     */
    public showMoreRequested = output<void>();

    /**
     * Emits when the table's state (filters, sort, pagination) changes.
     */
    public tableStateChange = output<any>();

    /**
     * Internal signal to hold the minimum duration timer state.
     */
    private _manualLoading = signal<boolean>(false);

    /**
     * Displayed loading state, computed from the loading input and the minimum duration timer.
     */
    public minDurationLoading = computed(() => this.loading() || this._manualLoading());

    /**
     * Skeleton visibility — suppressed while a show-more append is in flight so existing
     * rows stay visible and only the overlay loader shows.
     */
    public showSkeleton = computed(() => this.minDurationLoading() && !this._showMoreLoading());

    /**
     * Tracks when the loading state was last triggered.
     */
    private _loadingStartTime = 0;

    /**
     * Minimum duration to show the skeleton (in milliseconds).
     */
    private readonly MIN_LOADING_TIME = 2000;

    /**
     * Stores the last rendered data-row count so skeleton rows can match
     * the currently displayed number of table rows while loading.
     */
    private _lastRenderedRowCount = signal<number>(0);

    /**
     * Skeleton row indices rendered during loading state.
     * Uses current displayed count (or last rendered count) to avoid fixed placeholders.
     */
    public skeletonRowIndices = computed(() => {
        const currentDataRowCount = this.data().length;
        const resolvedRowCount =
            currentDataRowCount > 0 ? currentDataRowCount : this._lastRenderedRowCount();
        const rowCount = Math.max(this.MIN_SKELETON_ROWS, resolvedRowCount);

        return Array.from({ length: rowCount }, (_, index) => index);
    });

    constructor() {
        effect(() => {
            const isLoading = this.loading();
            const isShowMore = this._showMoreLoading();

            if (isShowMore) {
                this._manualLoading.set(false);
                return;
            }

            if (isLoading) {
                this._manualLoading.set(true);
                this._loadingStartTime = Date.now();
            } else {
                const elapsedTime = Date.now() - this._loadingStartTime;
                const remainingTime = Math.max(0, this.MIN_LOADING_TIME - elapsedTime);

                if (remainingTime > 0) {
                    setTimeout(() => {
                        this._manualLoading.set(false);
                    }, remainingTime);
                } else {
                    this._manualLoading.set(false);
                }
            }
        });

        effect(() => {
            const isLoading = this.loading();
            const isColumnFiltering = this.isColumnFiltering();
            const isOverlay = this.loadingOverlay();

            if (!this._showMoreLoading()) {
                this._showMoreSawLoading = false;
                return;
            }

            if (isLoading || isColumnFiltering || isOverlay) {
                this._showMoreSawLoading = true;
                return;
            }

            if (this._showMoreSawLoading) {
                this._showMoreLoading.set(false);
                this._showMoreSawLoading = false;
            }
        });

        effect(() => {
            const isLoading = this.minDurationLoading();
            const currentDataRowCount = this.data().length;

            if (!isLoading && currentDataRowCount > 0) {
                this._lastRenderedRowCount.set(currentDataRowCount);
            }
        });
    }

    /**
     * Subscribe to event emitted by the table actions component since we can't really use
     * output in this case since the emitted output goes to the ntv-table instead of in the app-table component
     * where we render the app-actions-table component
     * And hanles event emitting directly
     */
    public ngOnInit(): void {
        this._tableActionsService.actionClick
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: { action: string; row: any }) => {
                this.onActionClick(event.action, event.row);
            });
    }

    /**
     * Handles row selection changes from the internal ntv-table.
     * @param selectedRows - The array of currently selected rows
     */
    public onSelectionChange<T>(selectedRows: T[]): void {
        this.selectionChange.emit(selectedRows);
    }

    /**
     * Handles show-more requests and enables lightweight overlay mode.
     */
    public onShowMoreRequest(): void {
        this._showMoreLoading.set(true);
        this.showMoreRequested.emit();
    }

    /**
     * Handles "Show Less" clicks. Resets the pantry table's own internal
     * pagination counter (so a following "Show More" re-requests the right
     * page) before notifying the parent to collapse its accumulated rows.
     */
    public onShowLessClick(): void {
        this.ntvTable()?.resetPagination();
        this.showLessRequested.emit();
    }

    /**
     * Handles our own "Show More" button click by delegating to the pantry
     * table's own `showMore()` — it internally increments its page counter,
     * emits `tableStateChange`, and re-emits `showMoreRequested` (which our
     * `onShowMoreRequest()` above is already bound to).
     */
    public onShowMoreClick(): void {
        this.ntvTable()?.showMore();
    }

    /**
     * Handles action button click.
     * @param action - The action name/icon
     * @param row - The row data
     */
    public onActionClick(
        action: { name: string; icon: string; color: string; size: number } | string,
        row: any,
    ): void {
        const actionName = typeof action === 'string' ? action : action.name;
        this.actionClick.emit({ action: actionName, row });
    }

    /**
     * Handles clickable cell click.
     * @param field - The column field name
     * @param value - The cell value
     * @param row - The row data
     */
    public onCellClick(field: string, value: any, row: any): void {
        this.cellClick.emit({ field, value, row });
    }

    /**
     * Gets a custom template for a specific column field if it exists.
     * @param field The field name of the column.
     * @returns The TemplateRef if found, otherwise undefined.
     */
    public getCustomTemplate(field: string): TemplateRef<any> | undefined {
        return this._cellTemplates?.find((item) => item.columnName() === field)?.template;
    }

    /**
     * Checks if a value is an array.
     * @param val The value to check.
     * @returns True if the value is an array, false otherwise.
     */
    public isArray(val: any): boolean {
        return Array.isArray(val);
    }

    /**
     * Checks if a value is an object (and not null and not an array).
     * @param val The value to check.
     * @returns True if the value is a plain object, false otherwise.
     */
    public isObject(val: any): boolean {
        return val !== null && typeof val === 'object' && !Array.isArray(val);
    }

    /**
     * Resolves how many icons to show in a table actions column (max is 3)
     */
    public resolveTableActionsCount(
        actions: { name: string; icon: string; color: string; size: number }[],
    ): { name: string; icon: string; color: string; size: number }[] {
        return actions.length > 3 ? actions.slice(0, 2) : actions;
    }

    /**
     * Safely gets the type of a column.
     * @param col The column to check.
     * @returns The type of the column if it exists.
     */
    public getColumnType(col: TableColumn): string | undefined {
        return (col as any).type;
    }

    /**
     * Safely gets a property of a column.
     * @param col The column to check.
     * @param prop The property name to get.
     * @returns The value of the property if it exists.
     */
    public getColProp(col: TableColumn, prop: string): any {
        return (col as any)[prop];
    }
    /**
     * Returns true only when the cell value will be rendered with an ellipsis,
     * i.e. `truncateLimit` is set on the column and the resolved text exceeds it.
     *
     * @param rowData - The current row's data object.
     * @param col - The column definition.
     */
    public isTextTruncated(rowData: any, col: TableColumn): boolean {
        const limit: number = this.getColProp(col, 'truncateLimit') ?? 0;
        if (!limit) return false;
        return this.getTooltipText(rowData, col).length > limit;
    }

    /**
     * Returns the tooltip display text for a cell, handling arrays, objects, and primitives.
     *
     * Object resolution order: `label → value → name → JSON fallback`
     *
     * @param rowData - The current row's data object.
     * @param col - The column definition containing the `field` to look up.
     * @returns A human-readable string, or `''` if the value is nullish.
     */
    public getTooltipText(rowData: any, col: TableColumn): string {
        const val = rowData[col.field];
        if (this.isArray(val)) {
            return val
                .map((item: any) => {
                    if (this.isObject(item)) {
                        return item.label || item.value || item.name || JSON.stringify(item);
                    }
                    return item;
                })
                .join(', ');
        }
        if (this.isObject(val)) {
            return val.label || val.name || val.id || JSON.stringify(val);
        }
        return val?.toString() || '';
    }

    /**
     * Truncates the provided text to a specified limit.
     *
     * @param text The text to truncate.
     * @param limit The maximum number of characters. Defaults to 15.
     * @returns The truncated text with an ellipsis if it exceeds the limit.
     */
    public hasValue(val: any): boolean {
        if (val === null || val === undefined || val === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
    }

    public truncateText(text: any, limit: number = 0): string {
        if (text === null || text === undefined || text === '') return '--';
        const str = text.toString();
        if (limit === 0) return str;
        return str.length > limit ? str.slice(0, limit) + '...' : str;
    }
}
